/**
 * Licensed Materials - Property of IBM
 * IBM Business Analytics (C) Copyright IBM Corp. 2018, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

/**
 * @interface QueryResultAPI
 * @hideconstructor
 * @classdesc API class that is used to access a query result object.
 * @example queryResults.getResult()
 */
define([], () => {
	class QueryResultAPI {

		/**
		 * @function QueryResultAPI#getVersion
		 * @description Gets the query result version.
		 * @private
		 *
		 * @return {Number} Query result version
		 */
		getVersion() {}

		/**
		 * @function QueryResultAPI#hasMoreData
		 * @description Checks if the current query result is complete.
		 * @private
		 *
		 * @return {Boolean} true if query result data object includes hasNext or hasPrev equals to true
		 */
		hasMoreData() {}

		/**
		 * @function QueryResultAPI#getResultItemList
		 * @description Gets array of {@link ResultItemAPI}.
		 * @public
		 *
		 * @return {ResultItemAPI[]} Array of {@link ResultItemAPI}
		 */
		getResultItemList() {}

		/**
		 * @function QueryResultAPI#getResultItem
		 * @description Gets result data item with data item unique ID.
		 * @private
		 *
		 * @param {String} resultDataItemID Result data item unique ID
		 * @return {ResultItemAPI} {@link ResultItemAPI}
		 */
		getResultItem() {}

		/**
		 * @function QueryResultAPI#getValue
		 * @description Gets result data value based on row and column indexes.
		 * @public
		 *
		 * @param {Number} rowIndex Row index of the result data points
		 * @param {Number} resultDataItemIndex Data item index to get value in {@link ResultItemAPI}
		 * @return {ResultDataValue[] | ResultDataValue} Array of {@link ResultDataValue}
		 * @example
		 * // Category value
		 * [{
		 * 	value: 'dataset_csv.Product_line->[Camping Equipment]',
		 * 	label: 'Camping Equipment'
		 * }]
		 * @example
		 * // Measure value
		 * {
		 * 	value: 200
		 * }
		 */
		getValue() {}

		/**
		 * @function QueryResultAPI#getValueIndex
		 * @description Given an index in the result, return the index in the resultItem's values where this categorical tuple is found.
		 * @private
		 *
		 * @param {Number} rowIndex The row index (datapoint index)
		 * @param {Number} resultDataItemIndex The data item index to get value in {@link ResultItemAPI}
		 * @return {Number} A single number representing the index of the tuple.
		 * @example The first resultDataItem might be a nested categorical with values like 2004-Cars, 2004-Trucks, 2005-Cars, 2005-Trucks
		 * // getValueIndex({pointindex}, 0) would return 2 for any datapoint that contains 2005-Cars for the firstitem.
		 * 2
		 */
		getValueIndex() {}

		/**
		 * @function QueryResultAPI#getDataPointValueList
		 * @description Get the data points of the query result object.
		 * @private
		 *
		 * @return {Object[]} An array of data point object
		 * @example
		 * [{
		 * 	pt: [13, 4, {v: 151}, {v: 13834.62}]
		 * }]
		 */
		getDataPointValueList() {}


		/**
		 * @function QueryResultAPI#getRowCount
		 * @description Gets data row count.
		 *
		 * @return {Number} Number of rows of data in a query result object.
		 */
		getRowCount() {}

		/**
		 * @function QueryResultAPI#setPropertyValue
		 * @description Set a property value.
		 * @private
		 *
		 * @param {String} name Property name
		 * @param {String} value Property value
		 */
		setPropertyValue() {}

		/**
		 * @function QueryResultAPI#getPropertyValue
		 * @description Get a property value.
		 * @private
		 *
		 * @param {String} name Property name
		 * @return {String} Property value
		 */
		getPropertyValue() {}

		/**
		 * @public
		 * @function QueryResultAPI#getPointDecoration
		 * @description Get decorations for a datapoint
		 * @param {Number} rowIndex Numeric row index of the datapoint
		 * @return {Object} Object map containing decorations in name/value pairs. 
		 * Values are either a primitive value or an object representing the decoration.
		 * @example
		 *	{
		 *		"tooltip": [
		 *			["Confidence Upper Bound", "73.08"],
		 *			["Audience Score % (Average) Forecast", "60.82"],
		 *			["Confidence Lower Bound", "48.56"]
		 *		],
		 *		"forecastHighConfidence": 73.07758990139354,
		 *		"forecast": true,
		 *		"forecastValue": 60.8200277621683,
		 *		"forecastLowConfidence": 48.56246562294306
		 *	}
		 */
		getPointDecorations() {}
	}

	/**
	 * @private
	 * @readonly
	 * @description Enumeration of result value types
	 * @enum {string}
	 * @memberof QueryResultAPI
	 */
	QueryResultAPI.VALUE_TYPE = {
		/** Summary value type */
		SUMMARY: 'summary'
	};

	return QueryResultAPI;
});
